import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import { flashMessage } from '~/app/actions';
import EditComponentProp from '../PageEditor/EditComponentProp';
import { COMPONENT_VALUE_TYPES as types } from 'shared/constants';
import Button from '~/app/components/Button';
import { getS3AssetsManagement } from 'shared/lib/clientSettings';
import {
  getOverlaySettingsPath,
  getOverlaySettingsDraftPath,
  getPlaylistPath,
  getPlaylistDraftPath,
  getAssetFolderPath,
  PLAYLIST_JSON,
  PLAYLIST_DRAFT_JSON,
  OVERLAY_SETTINGS_JSON,
  OVERLAY_SETTINGS_DRAFT_JSON
} from 'shared/widgets/LivestreamVideoPlayer';
import { createUseStyles } from 'react-jss';
import Checkbox from '../PageEditor/Checkbox';
import Box from '@ff/ui/Box';

const UPLOAD_FILES = gql`
  mutation uploadFiles($files: [Upload!]!, $path: String!, $size: Int!) {
    # This file is rewritten in place on purpose: live pages poll it.
    uploadFiles(files: $files, path: $path, size: $size, replace: true) {
      Key
      Name: Key
      Size
      LastModified
      __typename
    }
  }
`;

/* this component overrides EditComponentProp and sync playlist with s3
* 1. it allows fetching playlist from s3 and update corresponding playlist props in EditComponentProp
* 2. it allows uploading playlist props in EditComponentProp to s3
 */
function PlaylistJsonUploader(props) {
  const { schema, value: playlistSettings, onChange, showFlashMessage } = props;
  const {
    polling = {}
  } = playlistSettings || {};

  const c = useStyles();
  const [uploadFilesMutation] = useMutation(UPLOAD_FILES);

  const [overlayEnabled, setOverlayEnabled] = useState(false);
  const [overlayDraftEnabled, setOverlayDraftEnabled] = useState(false);

  useEffect(() => {
    if (polling.enabled) {
      // clear playlist
      onChange('playlistSettings', { ...playlistSettings, playlist: [] });
    }

    const downloadOverlaySettings = async () => {
      try {
        const playlistPath = getOverlaySettingsPath(polling.path);
        const res = await fetch(`${getS3AssetsManagement()}/${playlistPath}?v=${Date.now()}`);
        const content = await res.json()

        showFlashMessage('Fetched overlay settings Successfully', 'SUCCESS');
        if (content.data) {
          setOverlayEnabled(content.data.enabled);
        }
      } catch (error) {
        console.error('Failed to fetch overlay settings', error);
        showFlashMessage('Failed to fetch overlay settings', 'ERROR');
      }
    }

    const downloadOverlaySettingsDraft = async () => {
      try {
        const playlistPath = getOverlaySettingsDraftPath(polling.path);
        const res = await fetch(`${getS3AssetsManagement()}/${playlistPath}?v=${Date.now()}`);
        const content = await res.json()

        showFlashMessage('Fetched overlay settings draft Successfully', 'SUCCESS');
        if (content.data) {
          setOverlayDraftEnabled(content.data.enabled);
        }
      } catch (error) {
        console.error('Failed to fetch overlay settings draft', error);
        showFlashMessage('Failed to fetch overlay settings draft', 'ERROR');
      }
    }

    downloadOverlaySettings();
    downloadOverlaySettingsDraft();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only run onload

  const download = async (playlistPath) => {
    try {
      const res = await fetch(`${getS3AssetsManagement()}/${playlistPath}?v=${Date.now()}`);
      const content = await res.json()

      showFlashMessage('Fetched Playlist Successfully', 'SUCCESS');
      if (content.data) {
        onChange('playlistSettings', { ...playlistSettings, playlist: content.data });
      }
    } catch (error) {
      console.error('Failed to fetch playlist', error);
      showFlashMessage('Failed to fetch playlist', 'ERROR');
    }
  }

  const upload = async (filename, data) => {
    // upload json playlist to s3, and allows LivestreamVideoPlayer to poll playlist later
    try {
      const file = new File(
        [JSON.stringify({ data })], // content
        filename,
        { type: 'text/json;charset=utf-8' }
      );

      await uploadFilesMutation({
        variables: {
          files: file,
          path: getAssetFolderPath(playlistSettings.polling.path),
          size: file.size,
        }
      });
      showFlashMessage('Uploaded Playlist Successfully', 'SUCCESS');
    } catch (error) {
      console.error('Failed to upload playlist', error);
      showFlashMessage('Failed to upload playlist', 'ERROR');
    }
  }

  const handleUpload = () => {
    if (!(playlistSettings?.playlist?.length > 0)) {
      return alert('playlist is empty');
    }

    if (confirm('Are you sure to upload?')) {
      upload(PLAYLIST_JSON, playlistSettings.playlist);
    }
  };

  const handleUploadDraft = () => {
    upload(PLAYLIST_DRAFT_JSON, playlistSettings.playlist);
  };

  const downloadPlaylist = () => {
    const playlistPath = getPlaylistPath(polling.path);
    download(playlistPath);
  };

  const downloadDraftPlaylist = () => {
    const playlistPath = getPlaylistDraftPath(polling.path);
    download(playlistPath);
  };

  const handleOverlayToggle = (value) => {
    setOverlayEnabled(value);
    upload(OVERLAY_SETTINGS_JSON, { enabled: value });
  };

  const handleDraftOverlayToggle = (value) => {
    setOverlayDraftEnabled(value);
    upload(OVERLAY_SETTINGS_DRAFT_JSON, { enabled: value });
  };

  return (
    <div>
      <div className={c.buttonContainer}>
        <div>Draft playlist only affects preview and editor</div>
        <Button
          className={c.button}
          onClick={handleUpload}
          primary
        >
          Upload playlist
        </Button>
        <Button
          className={c.button}
          onClick={downloadPlaylist}
          primary
        >
          Download playlist
        </Button>
        <Button
          className={c.button}
          onClick={handleUploadDraft}
          secondary
        >
          Upload draft playlist
        </Button>
        <Button
          className={c.button}
          onClick={downloadDraftPlaylist}
          secondary
        >
          Download draft playlist
        </Button>
        <Box my='20px'>
          <Checkbox
            label={'Enable Overlay (for Production)'}
            value={overlayEnabled}
            onChange={handleOverlayToggle}
          />
        </Box>
        <Box my='20px'>
          <Checkbox
            label={'Enable Draft Overlay (for Preview)'}
            value={overlayDraftEnabled}
            onChange={handleDraftOverlayToggle}
          />
        </Box>
      </div>

      <EditComponentProp
        {...props}
        schema={{
          ...schema,
          type: types.OBJECT,
          label: null // prevent duplicate title in EditComponentProp
        }}
      />
    </div >
  );
}

const useStyles = createUseStyles({
  buttonContainer: {
    marginLeft: 20
  },
  button: {
    marginRight: 20
  }
});

const mapDispatchToProps = (dispatch) => ({
  showFlashMessage: (...args) => dispatch(flashMessage(...args))
});

export default connect(null, mapDispatchToProps)(PlaylistJsonUploader);
